import { SnackBar } from "@jmc/solid-design-system/src/components/atoms/SnackBar/SnackBar";
import React, { useState } from "react";
import { mdiInformationOutline } from "@mdi/js";
import { default as path } from "@jmc/utils/utils/path";
import ReactIframeResizer from "react-iframe-resizer-super";
import analyticstracker from "@jmc/analyticstracker";
import { useLocalStorage } from "../../hooks/useLocalStorage/useLocalStorage";

export interface Props {
    src: string;
    visualSegregation?: boolean;
    fullSize?: boolean;
    options?: object;
    height?: number;
    unit?: "px" | "%" | "VH";
    text?: string;
    userInfo?: object;
}

/**
 * Creates an iFrame to embed content using ReactIframeResizer library.
 *
 * The ReactIframeResizer library will automatically add into the iframed content a client
 * JS snippet to control the height, but it can only do it if the iframed content is hosted
 * in the same domain.
 *
 * Otherwise, the client snippet needs to be added manually into the iframed content, for example, like this:
 *   <script
 *     src="https://raw.githubusercontent.com/davidjbradshaw/iframe-resizer/master/js/iframeResizer.contentWindow.min.js">
 *   </script>
 *
 * See: http://davidjbradshaw.github.io/iframe-resizer/
 */
export const Iframe = ({
    src,
    visualSegregation = false,
    fullSize,
    options,
    height = 400,
    unit = "px",
    text,
    userInfo = null,
}: Props): JSX.Element => {
    const [fullPath, setFullPath] = useState(null);
    const [basePath, setBasePath] = useState(null);
    const [logging] = useLocalStorage("iframeLogging", false);

    React.useEffect(() => {
        if (typeof window !== "undefined") {
            const basePath = window.location.origin;
            setBasePath(basePath);

            if (!window?.iframeTracking) {
                // Allow tracking only in iFrames hosted by us
                // The trackIFrames function must be called only once, or the events will be duplicated (i.e. multiple iframes in a page)
                analyticstracker().trackIFrames([basePath]);
                window.iframeTracking = true;
            }
        }
    }, []);

    React.useEffect(() => {
        let fullPath = "";
        if (src.startsWith("http") || src.indexOf("//") === 0) {
            // URL is absolute; either "http://example.com" or "//example.com"
            fullPath = src;
        } else {
            // URL is relative
            if (basePath) {
                fullPath = path.join(basePath, "/iframes", src);
            }
        }

        //Check if the user data is already set
        if (userInfo && fullPath) {
            const searchParams = new URLSearchParams(userInfo);
            if (new URL(fullPath).search == "") {
                fullPath += `?${searchParams.toString()}`;
            } else {
                if (new URL(fullPath).search == "?") {
                    fullPath += searchParams.toString();
                } else {
                    fullPath += `&${searchParams.toString()}`;
                }
            }
        }

        setFullPath(fullPath);
    }, [basePath, userInfo]);

    const iFrameHeight = `${height}${unit}`;
    const iframeResizerOptions = {
        log: logging,
        checkOrigin: false,
        ...options,
    };

    return visualSegregation ? (
        <>
            <SnackBar>
                <SnackBar.Icon icon={mdiInformationOutline} />
                <SnackBar.Text text={text} />
            </SnackBar>
            <div style={{ border: "10px solid var(--primary-100)" }} data-test-id="Iframe.VisualSegregation">
                <ReactIframeResizer
                    src={fullPath}
                    style={{ width: "100%", minHeight: iFrameHeight }}
                    iframeResizerOptions={iframeResizerOptions}
                />
            </div>
        </>
    ) : (
        <ReactIframeResizer
            src={fullPath}
            style={fullSize ? { width: "100%", height: "100%" } : { width: "100%", minHeight: iFrameHeight }}
            iframeResizerOptions={iframeResizerOptions}
        />
    );
};

export default Iframe;
